import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, useHistory, Redirect } from 'react-router-dom';
import { Subheader } from '@m12s/component-library';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';

import { Downtime } from 'components/Downtime';
import { Alarms } from 'components/Alarms';
import { Utilization } from 'components/Utilization';
import MainLayout from 'components/Layouts/MainLayout';
import { CycleTime } from 'components/CycleTime';
import { CycleCountStatus } from 'components/CycleCountStatus';
import { MachineStatus } from 'components/MachineStatus';
import { OEE } from 'components/OEE';
import { PartsGoal } from 'components/PartsGoal';
import Routes from 'lib/utils/routes';
import { CustomTab } from 'components/CustomTab';
import { getMachine } from 'lib/selectors/getMachine';
import { getScopeStart } from 'lib/selectors/getScopeStart';
import { getHomeTabRoute, getVisibleTabs } from 'lib/selectors/getTabs';
import { getCustomTabs } from 'lib/selectors/getCustomTab';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { getIsSetupModeActive } from 'lib/selectors/getIsSetupModeActive';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getIsWaitingForWork } from 'lib/selectors/getIsWaitingForWork';
import { getIsWaitingForOperator } from 'lib/selectors/getIsWaitingForOperator';
import { getIsUnknownJob } from 'lib/selectors/getIsUnknownJob';
import { getHasCards } from 'lib/selectors/getHasCards';
import { getHasUnmatchedCards } from 'lib/selectors/getHasUnmatchedCards';
import { APM_OPDASH_HOST, LABOR_TICKET_HOST } from 'lib/appConfig';
import {
  actionSetEmbeddedLaborTicket,
  actionOpenLaborTicketModal,
  actionSetApmCardContext,
} from 'lib/actions';
import MMEmbeddableZoneWithLoader from 'components/MMEmbeddableZoneWithLoader';
import MMDrawer from 'components/MMDrawer';
import EmbeddedMMDrawer from 'components/EmbeddedMMDrawer';
import { getLatestOperator } from 'lib/selectors/getLatestOperator';
import { getApmCardPartsContext } from 'lib/selectors/getApmCardPartsContext';
import { getFAIBannerVisible } from 'lib/selectors/getFAIBannerVisible';
import FAIBanner from 'components/FirstArticleInspection/FAIBanner';
import { SingleSelectJobDrawer } from 'components/SingleSelectJobDrawer';
import { getProgramEvents } from 'lib/selectors/getProgramEvents';
import {
  postSplitProgramEvents,
  postSubmitIntervals,
} from 'lib/api/multiJobSelectRest';
import { throwErrorToast, throwStatusToast } from 'lib/utils/toast';
import WaitingForWork from './MachineMainWrapper/WaitingForWork';
import WaitingForOperator from './MachineMainWrapper/WaitingForOperator';
import UnknownJob from './MachineMainWrapper/UnknownJob';
import Tab from './Tab';

const parseRestErrorMessage = (err) => {
  const raw = err?.message || 'An error occurred while submitting.';
  try {
    const parsed = JSON.parse(raw);
    return parsed.message || parsed.error || raw;
  } catch {
    return raw;
  }
};

const CONFIRM_WORK_EMBED_ZONE_ID = 'labor-ticket-confirm-work';

const ContentContainer = styled(MainLayout.Container)`
  background-color: #fafbfc;
`;

const MachineContent = ({ tab, openSelectJobDrawer }) => {
  const dispatch = useDispatch();
  const machine = useSelector(getMachine);
  const machineRef = machine?.machineRef;
  const scopeStart = useSelector(getScopeStart);
  const latestOperator = useSelector(getLatestOperator);
  const latestOperatorErpId = latestOperator?.erpId;
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);
  const isSetupMode = useSelector(getIsSetupModeActive);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const isWaitingForWork = useSelector(getIsWaitingForWork);
  const isWaitingForOperator = useSelector(getIsWaitingForOperator);
  const isUnknownJob = useSelector(getIsUnknownJob);
  const hasCards = useSelector(getHasCards);
  const hasUnmatchedCards = useSelector(getHasUnmatchedCards);
  const apmParts = useSelector(getApmCardPartsContext);

  // Program events are fetched once in DataProvider via useProgramEventDisplayState.
  const programEvents = useSelector(getProgramEvents);

  const [refreshToken, setRefreshToken] = useState(0);
  const [dismissedItemIds, setDismissedItemIds] = useState([]);
  const [optimisticSubmittedRefs, setOptimisticSubmittedRefs] = useState(() => {
    return new Set();
  });
  const optimisticSubmittedRefsList = useMemo(() => {
    return [...optimisticSubmittedRefs].sort((a, b) => {
      return a - b;
    });
  }, [optimisticSubmittedRefs]);

  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.dismissItemId;
      if (id) {
        setDismissedItemIds((prev) => {
          return [...prev, id];
        });
      }
      setRefreshToken((t) => {
        return t + 1;
      });
    };
    window.addEventListener('apm:opdash-refresh', handler);
    return () => {
      window.removeEventListener('apm:opdash-refresh', handler);
    };
  }, []);

  // Run Details flyup state
  const [runDetailsOpen, setRunDetailsOpen] = useState(false);
  const [runDetailsContext, setRunDetailsContext] = useState(null);

  // Confirm Work Produced drawer state
  const [confirmWorkOpen, setConfirmWorkOpen] = useState(false);
  const [confirmWorkContext, setConfirmWorkContext] = useState(null);
  const pendingSubmitRefsRef = useRef(new Set());

  // Closes the run-details drawer and clears the card context so a subsequent
  // labor ticket opened from a different path doesn't inherit stale parts data.
  const closeRunDetails = useCallback(() => {
    setRunDetailsOpen(false);
    setRunDetailsContext(null);
    dispatch(actionSetApmCardContext(null));
  }, [dispatch]);

  // select-different-job: user picks a different WOO (from NO_MATCH or SUGGESTED)
  useEmbeddedEffect(
    'apm-opdash-display',
    'apm-opdash:select-different-job',
    useCallback(
      (data) => {
        openSelectJobDrawer(
          data?.eventId
            ? {
                eventId: data.eventId,
                isChangeJob: true,
                sourceEventRefs: data.sourceEventRefs,
              }
            : null
        );
      },
      [openSelectJobDrawer]
    )
  );

  // Listen for CONFIRMED card tap → open run details flyup
  useEmbeddedEffect(
    'apm-opdash-display',
    'apm-opdash:view-run-details',
    useCallback(
      (data) => {
        dispatch(actionSetApmCardContext(data));
        setRunDetailsContext(data);
        setRunDetailsOpen(true);
      },
      [dispatch]
    )
  );

  // Listen for close from run details MFE
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:close',
    useCallback(() => {
      closeRunDetails();
    }, [closeRunDetails])
  );

  // Listen for confirmed from run details → close and refresh opdash cards
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:confirmed',
    useCallback(() => {
      closeRunDetails();
      setRefreshToken((t) => {
        return t + 1;
      });
    }, [closeRunDetails])
  );

  // Listen for voided from run details → close and refresh opdash cards
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:voided',
    useCallback(() => {
      setRunDetailsOpen(false);
      setRunDetailsContext(null);
      setRefreshToken((t) => {
        return t + 1;
      });
    }, [])
  );

  // Partial interval void — refresh cards without closing run details
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:intervals-voided',
    useCallback(() => {
      setRefreshToken((t) => {
        return t + 1;
      });
      window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
    }, [])
  );

  // Server caught up for optimistically submitted intervals — drop local overrides
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:submitted-confirmed',
    useCallback((data) => {
      const refs = data?.programEventRefs ?? [];
      if (refs.length === 0) return;
      setOptimisticSubmittedRefs((prev) => {
        const next = new Set(prev);
        refs.forEach((ref) => {
          next.delete(ref);
        });
        return next.size === prev.size ? prev : next;
      });
    }, [])
  );

  // Listen for ignored from run details → dismiss card immediately and refetch grouped
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:ignored',
    useCallback((data) => {
      setRunDetailsOpen(false);
      setRunDetailsContext(null);
      window.dispatchEvent(
        new CustomEvent('apm:opdash-refresh', {
          detail: { dismissItemId: data?.dismissItemId },
        })
      );
    }, [])
  );

  // Listen for change-job from run details → close details, open select-job drawer
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:change-job',
    useCallback(
      (data) => {
        closeRunDetails();
        openSelectJobDrawer(
          data?.eventId
            ? {
                eventId: data.eventId,
                isChangeJob: true,
                sourceEventRefs: data.sourceEventRefs,
              }
            : null
        );
      },
      [closeRunDetails, openSelectJobDrawer]
    )
  );

  // Listen for labor ticket click from run details → close details, open LT form
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:open-ticket',
    useCallback(
      (data) => {
        if (data?.laborTicketRef) {
          setRunDetailsOpen(false);
          setRunDetailsContext(null);
          dispatch(
            actionSetEmbeddedLaborTicket({
              laborTicketRef: data.laborTicketRef,
              personId: latestOperatorErpId,
            })
          );
          dispatch(actionOpenLaborTicketModal('submit'));
        }
      },
      [dispatch, latestOperatorErpId]
    )
  );

  // Split-change-job: open select-job drawer with split context
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:split-change-job',
    useCallback(
      (data) => {
        closeRunDetails();
        openSelectJobDrawer({ isSplitChangeJob: true }, data);
      },
      [closeRunDetails, openSelectJobDrawer]
    )
  );

  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:split-submit',
    useCallback(async (data) => {
      setRunDetailsOpen(false);
      setRunDetailsContext(null);
      try {
        await postSplitProgramEvents(
          data.machineRef,
          data.splitTime,
          data.workOrderOperationRef,
          null
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('split-submit failed', err);
      }
      setRefreshToken((t) => {
        return t + 1;
      });
    }, [])
  );

  // Listen for confirm-work-produced from run details → close details, open confirm form
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:confirm-work-produced',
    useCallback(
      (data) => {
        setRunDetailsOpen(false);
        setRunDetailsContext(null);

        if (data?.setupMode && data?.laborTicketRef) {
          dispatch(
            actionSetEmbeddedLaborTicket({
              laborTicketRef: data.laborTicketRef,
              personId: latestOperatorErpId,
              workOrderId: data?.jobNumber,
              sequenceNumber: data?.sequenceNumber,
              splitTime: data.splitTime,
            })
          );
          dispatch(actionOpenLaborTicketModal('submit'));
          return;
        }

        setConfirmWorkContext({
          machineRef: data?.machineRef ?? machineRef,
          workOrderOperationRef: data?.workOrderOperationRef,
          jobNumber: data?.jobNumber,
          partNumber: data?.partNumber,
          sequenceNumber: data?.sequenceNumber,
          finishQuantity: data?.finishQuantity ?? null,
          personId: latestOperatorErpId,
          intervals: data?.intervals ?? [],
          programEventRefs: data?.programEventRefs ?? [],
          wooCustomFieldsRaw: data?.wooCustomFieldsRaw ?? null,
          isSubmittingAllForJob: data?.isSubmittingAllForJob ?? false,
        });
        setConfirmWorkOpen(true);
      },
      [machineRef, latestOperatorErpId, dispatch]
    )
  );

  const handleConfirmWorkDismiss = useCallback(() => {
    setConfirmWorkOpen(false);
    setConfirmWorkContext(null);
  }, []);

  useEmbeddedEffect(
    CONFIRM_WORK_EMBED_ZONE_ID,
    'labor-ticket-form:submit-intervals-requested',
    useCallback(
      async (data) => {
        const programEventRefs =
          data?.programEventRefs ??
          (data?.intervals ?? []).map((interval) => {
            return interval.programEventRef;
          });
        const refsToSubmit = programEventRefs.filter((ref) => {
          return !pendingSubmitRefsRef.current.has(ref);
        });
        if (refsToSubmit.length === 0) {
          return;
        }
        refsToSubmit.forEach((ref) => {
          pendingSubmitRefsRef.current.add(ref);
        });

        const submitMachineRef = data?.machineRef ?? machineRef;
        const {
          machineRef: _machineRef,
          programEventRefs: _refs,
          ...apiPayload
        } = data;

        setConfirmWorkOpen(false);
        setConfirmWorkContext(null);
        setOptimisticSubmittedRefs((prev) => {
          return new Set([...prev, ...refsToSubmit]);
        });
        setRefreshToken((t) => {
          return t + 1;
        });
        window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
        throwStatusToast('Submitting…', 'info', { autoClose: 2000 });

        try {
          await postSubmitIntervals(submitMachineRef, apiPayload);
          setRefreshToken((t) => {
            return t + 1;
          });
          window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
        } catch (err) {
          setOptimisticSubmittedRefs((prev) => {
            const next = new Set(prev);
            refsToSubmit.forEach((ref) => {
              next.delete(ref);
            });
            return next;
          });
          throwErrorToast(parseRestErrorMessage(err));
        } finally {
          refsToSubmit.forEach((ref) => {
            pendingSubmitRefsRef.current.delete(ref);
          });
        }
      },
      [machineRef]
    )
  );

  const tabIdentifier = tab.id;
  switch (tabIdentifier) {
    case 'cycleCount':
      return <CycleCountStatus />;
    case 'jobs': {
      if (isApmERPJobFlow && !hasCards && !hasUnmatchedCards) {
        if (isUnknownJob) {
          return <UnknownJob />;
        }
        if (!currentWorkOrderOp) {
          if (isWaitingForWork) return <WaitingForWork />;
          if (isWaitingForOperator) return <WaitingForOperator />;
        }
      }
      return (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <MMEmbeddableZoneWithLoader
            id="apm-opdash-display"
            src={APM_OPDASH_HOST}
            path="/"
            context={{
              machineRef: machine?.machineRef,
              machineId: machine?.id,
              start: scopeStart,
              erpResources: machine?.erpResources,
              refreshToken,
              dismissedItemIds,
              personId: latestOperatorErpId,
              programEvents,
              setupMode: isSetupMode,
            }}
          />
          <MMDrawer
            isOpen={runDetailsOpen}
            onClose={closeRunDetails}
            title="Production Run Details"
          >
            <MMEmbeddableZoneWithLoader
              id="apm-opdash-run-details"
              src={APM_OPDASH_HOST}
              path="/run-details"
              context={{
                start: scopeStart,
                erpResources: machine?.erpResources,
                personId: latestOperatorErpId,
                programEvents,
                refreshToken,
                optimisticSubmittedRefs: optimisticSubmittedRefsList,
                ...runDetailsContext,
                machineRef: machine?.machineRef,
                partsContext: apmParts ? { apmParts } : undefined,
              }}
            />
          </MMDrawer>
          <EmbeddedMMDrawer
            embedZoneId={CONFIRM_WORK_EMBED_ZONE_ID}
            messageSource="labor-ticket-form"
            src={LABOR_TICKET_HOST}
            path="/confirm-work-produced"
            context={confirmWorkContext}
            isOpen={confirmWorkOpen}
            onDismiss={handleConfirmWorkDismiss}
            defaultTitle="Confirm work produced"
          />
        </div>
      );
    }
    case 'partCount':
      return <MachineStatus />;
    case 'utilization':
      return <Utilization />;
    case 'downtime':
      return <Downtime />;
    case 'alarms':
      return <Alarms />;
    case 'partsGoal':
      return <PartsGoal />;
    case 'cycleTime':
      return <CycleTime />;
    case 'oee':
      return <OEE />;
    default:
      return null;
  }
};

const MachineMain = ({ tab: currentTab }) => {
  const history = useHistory();
  const machine = useSelector(getMachine);
  const baseTabs = useSelector(getVisibleTabs);
  const scopeStart = useSelector(getScopeStart);
  const baseHomeTabRoute = useSelector(getHomeTabRoute);
  const machineId = machine?.id;
  const customTabs = useSelector(getCustomTabs);
  const faiBannerVisible = useSelector(getFAIBannerVisible);
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);
  const currentOperator = useSelector(getCurrentOperator);
  const windowPath = window.location.pathname;

  // --- Single-select job drawer state (lives here so it survives tab switches) ---
  const [selectJobDrawerOpen, setSelectJobDrawerOpen] = useState(false);
  const [drawerApmFlow, setDrawerApmFlow] = useState(null);
  const [drawerSplitContext, setDrawerSplitContext] = useState(null);

  const openSelectJobDrawer = useCallback((navState, splitPayload) => {
    setDrawerApmFlow(navState || null);
    setDrawerSplitContext(splitPayload || null);
    setSelectJobDrawerOpen(true);
  }, []);

  const completeSelectJobDrawer = useCallback(() => {
    setSelectJobDrawerOpen(false);
    setDrawerApmFlow(null);
    setDrawerSplitContext(null);
    window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
  }, []);

  const selectJobFlowContextValue = useMemo(() => {
    return {
      apmFlowState: drawerApmFlow,
      splitJobContext: drawerSplitContext,
      onApmFlowComplete: completeSelectJobDrawer,
    };
  }, [drawerApmFlow, drawerSplitContext, completeSelectJobDrawer]);

  useEffect(() => {
    if (isApmERPJobFlow && !currentOperator && selectJobDrawerOpen) {
      completeSelectJobDrawer();
    }
  }, [
    isApmERPJobFlow,
    currentOperator,
    selectJobDrawerOpen,
    completeSelectJobDrawer,
  ]);

  useEffect(() => {
    const handler = () => {
      if (isApmERPJobFlow && !currentOperator) {
        return;
      }
      openSelectJobDrawer(null);
    };
    window.addEventListener('apm:open-select-job-drawer', handler);
    return () => {
      window.removeEventListener('apm:open-select-job-drawer', handler);
    };
  }, [openSelectJobDrawer, isApmERPJobFlow, currentOperator]);

  // For APM ERP machines, inject a "Jobs" tab at the front of the list
  const tabs = useMemo(() => {
    if (isApmERPJobFlow) {
      return [{ id: 'jobs', enabled: true }, ...baseTabs];
    }
    return baseTabs;
  }, [baseTabs, isApmERPJobFlow]);

  // Recompute home route based on the (possibly augmented) tab list
  const homeTabRoute = useMemo(() => {
    if (tabs.length && machineId) {
      return Routes.machineIdHomeTabPath(machineId, tabs[0].id);
    }
    return baseHomeTabRoute;
  }, [tabs, machineId, baseHomeTabRoute]);

  if (!scopeStart || !homeTabRoute) {
    return null;
  }

  if (faiBannerVisible) {
    return <FAIBanner />;
  }

  return (
    <>
      <Subheader
        defaultValue={currentTab}
        onChange={(value) => {
          return history.push(Routes.machineIdHomeTabPath(machine.id, value));
        }}
      >
        {tabs.length > 1 &&
          tabs.map((tab) => {
            return <Tab key={tab.id} tab={tab} />;
          })}
      </Subheader>
      <ContentContainer>
        {customTabs.map((customTab) => {
          return (
            <CustomTab
              key={customTab.id}
              tab={customTab}
              base={Routes.machineIdHomeTabPath(machine.id, customTab.id)}
              hidden={
                windowPath !==
                Routes.machineIdHomeTabPath(machine.id, customTab.id)
              }
            />
          );
        })}
        <Switch>
          {tabs.map((tab) => {
            return (
              <Route
                key={tab.id}
                path={Routes.machineIdHomeTabPath(machine.id, tab.id)}
              >
                <MachineContent
                  tab={tab}
                  openSelectJobDrawer={openSelectJobDrawer}
                />
              </Route>
            );
          })}
          <Route path={Routes.machineIdHomePath(machine.id)}>
            <Redirect to={homeTabRoute} />
          </Route>
        </Switch>
      </ContentContainer>
      {isApmERPJobFlow && (
        <SingleSelectJobDrawer
          isOpen={selectJobDrawerOpen}
          onClose={completeSelectJobDrawer}
          flowContextValue={selectJobFlowContextValue}
        />
      )}
    </>
  );
};

MachineMain.propTypes = {
  tab: PropTypes.string.isRequired,
};

export default MachineMain;
